import {
  Organisation,
  OrgSiteListEntry,
} from "@inrange/building-manager-api-client/models-organisation";
import {
  getFinancialPerformance,
  getFinancialPerformanceProcurement,
} from "@inrange/building-manager-api-client/models-site";
import {
  isSiteOwnerOccupied,
  NoPaddedRow,
  TableListSortFilters,
  TableListToggleButtons,
} from "@inrange/theme-components";
import {
  OperationalTenantSiteListHeader,
  OperationalTenantSiteRow,
  Table,
} from "@inrange/theme-components/table";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../auth/UserContext";
import { isDemoModeEnabled } from "../routes/SiteViewTabs/demoMode";
import {
  filterSitesByText,
  sortAscendingForColumn,
  sortByColumn,
  togglesortAscending,
} from "./SiteListSortMethods";
import { getLatestActualsMonthIndex } from "./sitelist-utils";

interface OperationalTenantSiteListViewProps {
  operationalSites: OrgSiteListEntry[];
  organisation: Organisation;
  startingSortColumn: string;
}

const groupSitesByOwnedOrLeased = (
  sites: OrgSiteListEntry[],
  orgID: string
) => {
  const ownedOwnerships = ["ownerOccupier", "landlord"]; // Here we treat landlord and owner occupier as owned
  return sites.reduce(
    (acc, site) => {
      const siteOwnership = site.siteOwnerships.find(
        (siteOwnership) => siteOwnership.orgID === orgID
      )!;
      if (ownedOwnerships.includes(siteOwnership.ownership)) {
        acc["owned"].push(site);
      } else {
        acc["leased"].push(site);
      }
      return acc;
    },
    { owned: [] as OrgSiteListEntry[], leased: [] as OrgSiteListEntry[] }
  );
};

const OperationalTenantSiteListView: React.FC<
  OperationalTenantSiteListViewProps
> = ({ operationalSites, organisation, startingSortColumn }) => {
  const sitesByOwnedOrLeased = useMemo(
    () => groupSitesByOwnedOrLeased(operationalSites, organisation.id),
    [operationalSites, organisation.id]
  );
  const showOwnershipModelSelector =
    sitesByOwnedOrLeased["owned"].length > 0 &&
    sitesByOwnedOrLeased["leased"].length > 0;
  const defaultOwnershipModel =
    sitesByOwnedOrLeased["owned"].length > 0 ? "owned" : "leased";
  const defaultSites = sitesByOwnedOrLeased[defaultOwnershipModel];
  const [textFilter, setTextFilter] = useState("");
  const [filteredSites, setFilteredSites] =
    useState<OrgSiteListEntry[]>(defaultSites);
  const [sortColumn, setSortColumn] = useState(startingSortColumn);
  const [sortAscending, setSortAscending] = useState(
    sortAscendingForColumn(startingSortColumn)
  );
  const [ownershipModel, setOwnershipModel] = useState(defaultOwnershipModel);
  const { user } = useContext(UserContext);

  const changeOwnershipModel = (model: string) => {
    setSortColumn(startingSortColumn);
    setSortAscending(sortAscendingForColumn(startingSortColumn));
    setOwnershipModel(model);
    setFilteredSites(sitesByOwnedOrLeased[model]);
  };

  const modelSelectionTabs = {
    tab1: {
      label: "Owned",
      active: ownershipModel === "owned",
      onClick: () => {
        changeOwnershipModel("owned");
      },
      count: sitesByOwnedOrLeased["owned"].length,
    },
    tab2: undefined,
    tab3: {
      label: "Leased",
      active: ownershipModel === "leased",
      onClick: () => {
        changeOwnershipModel("leased");
      },
      count: sitesByOwnedOrLeased["leased"].length,
    },
  };

  // Sort and filter
  useEffect(() => {
    let listedSites = [...sitesByOwnedOrLeased[ownershipModel]];
    if (textFilter.length > 0) {
      listedSites = filterSitesByText(listedSites, textFilter);
    }
    sortByColumn(listedSites, sortColumn, sortAscending, organisation);
    setFilteredSites(listedSites);
  }, [
    textFilter,
    organisation,
    sortColumn,
    sortAscending,
    sitesByOwnedOrLeased,
    ownershipModel,
  ]);

  const handleColumnClick = (columnKey: string) => {
    if (sortColumn === columnKey) {
      togglesortAscending(setSortAscending);
      return;
    }
    setSortColumn(columnKey);
    setSortAscending(sortAscendingForColumn(columnKey));
  };

  const hasAnyTakeOrPaySite = filteredSites.some(
    (site) => site.tariffConfig.takeOrPay
  );

  const isOfftakerOnly = filteredSites.every(
    (site) => site.totalInstalledCapacity === 0
  );

  const showHealthIndicator = isDemoModeEnabled(organisation.demo, user);

  const tableHead = (
    <OperationalTenantSiteListHeader
      mode={ownershipModel}
      sortColumn={sortColumn}
      sortAscending={sortAscending}
      handleColumnClick={handleColumnClick}
      hideSavings={hasAnyTakeOrPaySite}
      isOfftakerOnly={isOfftakerOnly}
      showHealthIndicator={showHealthIndicator}
    />
  );

  const tableRows = filteredSites.map((site) => {
    let lastMonthCost = 0;
    let lastMonthProcurement = 0;
    let lastMonthRevenue = 0;
    let lastMonthDemandFulfilled = 0;
    let lastMonthSavings = 0;
    let lastMonthGeneration = 0;
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex !== undefined) {
      const lastMonthEnergyFlows =
        site.energyFinancialsMonthly[lastMonthIndex].energy.actual!;
      const lastMonthFinancialModels =
        site.energyFinancialsMonthly[lastMonthIndex].financial.actual!;
      const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
      const procurement = getFinancialPerformanceProcurement(
        lastMonthFinancialModels,
        isOwnerOccupier,
        site.activeInvestmentModel
      )!;
      const revenue = getFinancialPerformance(
        lastMonthFinancialModels,
        isOwnerOccupier,
        site.activeInvestmentModel
      )!;
      lastMonthCost = procurement.costTotal;
      lastMonthProcurement = lastMonthEnergyFlows.procurement;
      lastMonthRevenue = revenue.revenueTotal;
      lastMonthDemandFulfilled =
        lastMonthEnergyFlows.procurement / lastMonthEnergyFlows.demand;
      lastMonthSavings = procurement.savings;
      lastMonthGeneration = lastMonthEnergyFlows.generation;
    }

    return (
      <OperationalTenantSiteRow
        key={site.id}
        site={site}
        organisation={organisation}
        mode={ownershipModel}
        lastMonthCost={lastMonthCost}
        lastMonthProcurement={lastMonthProcurement}
        lastMonthRevenue={lastMonthRevenue}
        lastMonthDemandFulfilled={lastMonthDemandFulfilled}
        lastMonthSavings={lastMonthSavings}
        lastMonthGeneration={lastMonthGeneration}
        hideSavings={hasAnyTakeOrPaySite}
        isOfftakerOnly={isOfftakerOnly}
        showHealthIndicator={showHealthIndicator}
      />
    );
  });

  return (
    <>
      <NoPaddedRow style={{ marginTop: "8px" }}>
        {showOwnershipModelSelector && (
          <TableListToggleButtons tabs={modelSelectionTabs} />
        )}
        <TableListSortFilters
          textFilter={textFilter}
          setTextFilter={setTextFilter}
        />
      </NoPaddedRow>
      <NoPaddedRow>
        <Table head={tableHead} rows={tableRows} />
      </NoPaddedRow>
    </>
  );
};

export default OperationalTenantSiteListView;
